import { get } from 'lodash';

import type {
  ColumnMappingValues,
  FinalMappedData,
  Subsidiary,
} from '@revelio/core';
import type { Item } from '@revelio/iso-utility';

import { environment } from '../../../../environments/environment';
import type { CompanyResultItem } from '../../../../shared/types/company-result-item';

export const transformMappingData = (
  mappingsData: Record<string, ColumnMappingValues>
): FinalMappedData[] => {
  const selectedPrimaryCompanies = get(
    mappingsData,
    ['primary', 'subsidiaries'],
    []
  ).filter((primary) => primary.isSelected);
  return selectedPrimaryCompanies.map((primary) => {
    const child_rcids: number[] = [];
    const expand_rcids: number[] = [];
    const flattenNestedSubsidiaries = (company: Subsidiary) => {
      const hasLoadedSubsidiaries =
        // @ts-expect-error noUncheckedIndexedAccess
        mappingsData[company.id] && mappingsData[company.id].subsidiaries;
      if (company.hasSubsidiaries && hasLoadedSubsidiaries) {
        child_rcids.push(Number(company.rcid));
        hasLoadedSubsidiaries.forEach((subsidiary) => {
          if (subsidiary.isSelected) {
            flattenNestedSubsidiaries(subsidiary);
          }
        });
      }

      if (
        company.hasSubsidiaries &&
        !hasLoadedSubsidiaries &&
        company.isSelected
      ) {
        expand_rcids.push(Number(company.rcid));
      }

      if (!company.hasSubsidiaries && company.isSelected) {
        child_rcids.push(Number(company.rcid));
      }
    };

    flattenNestedSubsidiaries(primary);

    return {
      name: primary.title,
      rcid: Number(primary.rcid),
      child_rcids,
      expand_rcids,
      company_id: primary.company_id ?? String(primary.rcid),
    };
  });
};

export const formatSubsidiaries = (
  sub: CompanyResultItem | Item
): Subsidiary => ({
  rcid: Number(sub.rcid),
  title: String(sub.primary_name),
  id: String(sub.primary_name).split(' ').join('-'),
  company_id: (sub.company_id as string | undefined) ?? String(sub.rcid),
  headCount: Number(sub.raw_emp_count),
  hasSubsidiaries: Boolean(sub.has_subsidiaries),
  isSelected: true,
  isIndeterminate: false,
});

export const fetchSubsidiaries = async (
  rcid: number,
  endpoint = 'subsidiaries'
) => {
  try {
    const response: Response = await fetch(
      `${environment.COMPANY_MAPPER_URL}/${endpoint}`,
      {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ rcid: Number(rcid) }),
        credentials: 'include',
      }
    );
    if (!response.ok) {
      throw new Error('There was an error!');
    }
    const result: {
      company_id: string;
      rcid: number;
      raw_emp_count: number;
      primary_name: string;
      has_subsidiaries: boolean;
    }[] = await response.json();

    const companySubsidiaries = result
      .map(formatSubsidiaries)
      .sort((a, b) => b.headCount - a.headCount);

    return companySubsidiaries;
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
};
